import { CreditCard, Receipt, Warning as WarningIcon } from '@factorialco/f0-react/icons/app'
import { ExpensablesDataCollectionQuery } from 'generated/resources/graphql'

import buildUrl from 'modules/core/lib/buildUrl'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { CustomizationField, fieldOptions } from 'modules/expenses/hooks/useExpensesCustomization'
import { getCardTypeLabel } from 'modules/expenses/lib/cards/getCardTypeLabel'
import { getSpendingAlertsDisplay } from 'modules/expenses/lib/expensables/spendingAlertsHelper'
import { getStatusColor } from 'modules/expenses/lib/expensables/statusColor'
import { getStatusLabel } from 'modules/expenses/lib/expensables/statusLabel'
import { amountWithCurrency } from 'modules/finance/lib/budgets'

type Expensable =
  ExpensablesDataCollectionQuery['expenses']['expensablesConnection']['nodes'][number]

const formatCardInfo = (cardType: string, last4: string | null | undefined): string => {
  const last4Label = last4 ? ` - ${last4}` : '****'

  if (cardType === 'physical' || cardType === 'virtual' || cardType === 'single_use') {
    return `${getCardTypeLabel(cardType)} ${last4Label}`
  }
  return ''
}

interface Props {
  isMyExpenses?: boolean
  locale: string
  customizationFields?: CustomizationField[]
  showGroupColumn?: boolean
}
export const useExpensesDataCollection = ({
  isMyExpenses = false,
  locale,
  customizationFields = [],
  showGroupColumn = true,
}: Props) => {
  const getDocumentTypeLabel = (documentType: string | null | undefined): string => {
    if (!documentType || !customizationFields.length) return ''

    const documentTypeOptions = fieldOptions('expense', 'document_type', customizationFields)
    const option = documentTypeOptions.find((el) => el.value === documentType)
    return option?.label || ''
  }
  const getPaymentMethodLabel = (paymentMethod: string | null | undefined): string => {
    if (!paymentMethod || !customizationFields.length) return ''

    const paymentMethodOptions = fieldOptions('expense', 'payment_method', customizationFields)
    const option = paymentMethodOptions.find((el) => el.id === paymentMethod)
    return option?.label || ''
  }

  return {
    sortings: {
      effectiveOn: {
        label: i18n.t('expenses_v2.list.header.date'),
      },
      status: {
        label: i18n.t('expenses_v2.list.header.status'),
      },
      amount: {
        label: i18n.t('expenses_v2.list.header.amount'),
      },
      createdAt: {
        label: i18n.t('expenses_v2.list.header.created_at'),
      },
      reimbursableAmount: {
        label: i18n.t('expenses_v2.list.header.reimbursable_amount'),
      },
    },
    defaultSortings: {
      field: 'effectiveOn' as const,
      order: 'desc' as const,
    },
    columns: [
      ...(isMyExpenses
        ? [
            {
              label: i18n.t('expenses_v2.list.header.merchant_name'),
              render: (item: Expensable) => {
                let label
                if (item.mileage) {
                  label = i18n.t('expenses_v2.expense.mileage_expense')
                } else if (item.perDiem) {
                  label = i18n.t('expenses_v2.expense.per_diem_expense')
                } else label = item.expense?.userMerchant

                return {
                  type: 'text' as const,
                  value: label,
                }
              },
            },
          ]
        : [
            {
              label: i18n.t('expenses_v2.list.header.owner'),
              render: (item: Expensable) => ({
                type: 'person' as const,
                value: {
                  firstName: item.owner?.access.fullName,
                  lastName: '',
                  src: item.owner?.access.avatar ? buildUrl(item.owner.access.avatar) : undefined,
                },
              }),
            },
          ]),
      {
        label: i18n.t('expenses_v2.list.header.status'),
        sorting: 'status' as const,
        render: (item: Expensable) => ({
          type: 'status' as const,
          value: {
            label: getStatusLabel(item.status),
            status: getStatusColor(item.status),
          },
        }),
      },
      {
        label: i18n.t('expenses_v2.list.header.date'),
        sorting: 'effectiveOn' as const,
        render: (item: Expensable) => ({
          type: 'date' as const,
          value: dateBackendDateTimeToUI(item.effectiveOn),
        }),
      },
      {
        label: i18n.t('expenses_v2.list.header.amount'),
        sorting: 'amount' as const,
        render: (item: Expensable) => ({
          type: 'text' as const,
          value: amountWithCurrency(Number(item.amount), item.currency, locale),
        }),
      },
      {
        label: i18n.t('expenses_v2.list.header.category'),
        render: (item: Expensable) => {
          const expensable = item.expense || item.mileage || item.perDiem
          return {
            type: 'tag' as const,
            value: {
              label:
                expensable?.category?.name ?? i18n.t('expenses_v2.expense.category.uncategorized'),
            },
          }
        },
      },
      {
        label: i18n.t('expenses_v2.list.header.spending_alerts'),
        render: (item: Expensable) => {
          const alertDisplay = getSpendingAlertsDisplay(
            item.spendingAlertsTarget,
            item.spendingAlertsSource
          )

          return {
            type: 'alertTag' as const,
            value: {
              label: alertDisplay?.label,
              icon: WarningIcon,
              level: alertDisplay?.color,
            },
          }
        },
      },
      ...(showGroupColumn
        ? [
            {
              label: i18n.t('expenses_v2.list.header.group'),
              noHiding: true,
              render: (item: Expensable) => ({
                type: 'folder' as const,
                value: item.expensableGroup ? { name: item.expensableGroup.name } : undefined,
              }),
            },
          ]
        : []),
      {
        label: i18n.t('expenses_v2.list.header.created_at'),
        sorting: 'createdAt' as const,
        hidden: true,
        render: (item: Expensable) => ({
          type: 'date' as const,
          value: item.createdAt ? dateBackendDateTimeToUI(item.createdAt) : '',
        }),
      },
      {
        label: i18n.t('expenses_v2.list.header.document_type'),
        hidden: true,
        render: (item: Expensable) => {
          const documentType = item.expense?.documentType
          const label = getDocumentTypeLabel(documentType)
          if (!label) {
            return {
              type: 'text' as const,
              value: '',
            }
          }
          return {
            type: 'icon' as const,
            value: {
              label,
              icon: Receipt,
            },
          }
        },
      },
      {
        label: i18n.t('expenses_v2.list.header.payment_method'),
        hidden: true,
        render: (item: Expensable) => {
          let label = ''
          if (item.expense) {
            const { creationType, paymentMethod, card } = item.expense

            if (creationType === 'manual') {
              label = getPaymentMethodLabel(paymentMethod)
            }
            if (card) {
              label = formatCardInfo(card.cardType, card.last4)
            }
          }
          if (!label)
            return {
              type: 'text' as const,
              value: '',
            }
          return {
            type: 'icon' as const,
            value: {
              label: label,
              icon: CreditCard,
            },
          }
        },
      },
      {
        label: i18n.t('expenses_v2.list.header.reimbursable_amount'),
        sorting: 'reimbursableAmount' as const,
        hidden: true,
        render: (item: Expensable) => {
          if (!item.reimbursableAmount || !item.reimbursableCurrency) {
            return {
              type: 'text' as const,
              value: '',
            }
          }
          return {
            type: 'text' as const,
            value: amountWithCurrency(
              Number(item.reimbursableAmount),
              item.reimbursableCurrency,
              locale
            ),
          }
        },
      },
      {
        label: i18n.t('expenses_v2.list.header.internal_reference'),
        hidden: true,
        render: (item: Expensable) => ({
          type: 'text' as const,
          value: item.internalReference || '',
        }),
      },
    ],
  }
}
